/**
 * Reserved environment variable names (SPEC §12.1). A variable defined in Hangar reaches processes Hangar does not
 * control: the CI process that runs `eas build` (`clientExecution.processEnv`) and the provider build (`/runtime/env`).
 * A name that steers how programs are found, loaded or run (`PATH`, `NODE_OPTIONS`, `LD_PRELOAD`, `BASH_ENV`…), where
 * tools read their configuration and credentials (`HOME`, `npm_config_*`, `GIT_*`, `SSH_*`) or that belongs to Hangar,
 * EAS or the CI credential (`HANGAR_*`, `EAS_*`, `EXPO_TOKEN`) would turn `env:write` into code execution in CI or a
 * redirected build. So these names can never be defined: the API refuses them on every write (`422`, code
 * `RESERVED_KEY`) and never delivers one stored before that rule; the CLI never passes one from a snapshot to `eas`.
 * Names are compared case-insensitively. `EXPO_PUBLIC_*` and ordinary app keys (`APP_ENV`, `SENTRY_DSN`) are untouched.
 * A denylist cannot be exhaustive: it covers the programs on the build path (shells, the dynamic loader, Node.js, the
 * JVM, npm/yarn, git, ssh) plus Hangar's and EAS's own names.
 *
 * Source of truth for the API and the CLI. The published CLI (`@malible/hangar`) cannot depend on this private package,
 * so packages/cli/src/env-keys.ts is a byte-identical copy (`cp packages/shared/src/env-keys.ts packages/cli/src/`);
 * packages/cli/tests/env-keys.test.ts fails when they differ. No imports on purpose.
 */

export interface ReservedEnvKeyRule {
  /** An exact name (`PATH`) or a prefix ending in `*` (`LD_*`), upper case; keys are compared case-insensitively. */
  readonly pattern: string;
  /** Why the name is reserved: quoted by the API's 422 and its `RESERVED_KEY` warning. */
  readonly reason: string;
}

export const RESERVED_ENV_KEYS: readonly ReservedEnvKeyRule[] = [
  // Program lookup and loading
  { pattern: 'PATH', reason: 'decides which programs run' },
  { pattern: 'LD_*', reason: 'dynamic loader settings (LD_PRELOAD, LD_LIBRARY_PATH) load code into every program' },
  { pattern: 'DYLD_*', reason: 'macOS dynamic loader settings load code into every program' },
  // Runtimes that read options from the environment
  { pattern: 'NODE_OPTIONS', reason: 'adds flags, and code (--require, --import), to every Node.js process' },
  { pattern: 'NODE_PATH', reason: 'changes where Node.js resolves modules' },
  { pattern: 'NODE_EXTRA_CA_CERTS', reason: 'adds trusted certificate authorities to Node.js (TLS interception)' },
  { pattern: 'NODE_TLS_REJECT_UNAUTHORIZED', reason: 'turns off TLS certificate checks in Node.js' },
  { pattern: 'JAVA_TOOL_OPTIONS', reason: 'adds options, and agents, to every JVM (Gradle)' },
  { pattern: '_JAVA_OPTIONS', reason: 'adds options, and agents, to every JVM (Gradle)' },
  { pattern: 'JDK_JAVA_OPTIONS', reason: 'adds options, and agents, to every JVM (Gradle)' },
  // Shells
  { pattern: 'BASH_ENV', reason: 'a file every non-interactive bash runs first (its value is expanded, command substitution included)' },
  { pattern: 'ENV', reason: 'a file sh runs at startup (its value is expanded)' },
  { pattern: 'BASH_FUNC_*', reason: 'exported bash functions replace commands' },
  { pattern: 'SHELLOPTS', reason: 'sets bash options (xtrace with PS4 runs commands)' },
  { pattern: 'BASHOPTS', reason: 'sets bash options' },
  { pattern: 'PS4', reason: 'expanded for every traced bash command' },
  { pattern: 'SHELL', reason: 'the shell tools start for subcommands' },
  { pattern: 'IFS', reason: 'changes how shells split words' },
  // Where tools read configuration and credentials, and write temporary files
  { pattern: 'HOME', reason: 'where tools read their configuration and credentials (.npmrc, .gitconfig, .ssh)' },
  { pattern: 'TMPDIR', reason: 'where programs write temporary files' },
  { pattern: 'TMP', reason: 'where programs write temporary files' },
  { pattern: 'TEMP', reason: 'where programs write temporary files' },
  // Package managers
  { pattern: 'NPM_CONFIG_*', reason: 'npm and npx configuration (registry, script-shell, node-options)' },
  { pattern: 'YARN_*', reason: 'yarn configuration (registry, plugins)' },
  { pattern: 'COREPACK_*', reason: 'where corepack downloads package managers from' },
  // git and ssh
  { pattern: 'GIT_*', reason: 'git configuration and helpers (GIT_SSH_COMMAND, GIT_CONFIG_*, GIT_ASKPASS) run commands' },
  { pattern: 'SSH_*', reason: 'the ssh agent socket and askpass program' },
  // Hangar, EAS and the CI credential
  { pattern: 'HANGAR_*', reason: "Hangar's own variables (build id and number, API URL, runtime token)" },
  { pattern: 'EAS_*', reason: 'eas-cli and EAS Build settings' },
  { pattern: 'EXPO_TOKEN', reason: "the CI's Expo access token" },
];

/**
 * The `HANGAR_*` names Hangar itself puts in a build's environment: the snapshot's system entries (environment,
 * platform, app, profile), the per-build values the API adds (build id and number, version name, API URL) and the
 * runtime token the CLI adds. Reserved for user variables, they are the only `HANGAR_*` values a client accepts from
 * `clientExecution.processEnv`; the CLI then sets its own `HANGAR_*` values on top, which always win.
 */
export const HANGAR_SYSTEM_ENV_KEYS: readonly string[] = [
  'HANGAR_ENVIRONMENT', 'HANGAR_PLATFORM', 'HANGAR_APP', 'HANGAR_PROFILE',
  'HANGAR_BUILD_ID', 'HANGAR_BUILD_NUMBER', 'HANGAR_VERSION_NAME', 'HANGAR_API_URL', 'HANGAR_RUNTIME_TOKEN',
];

/** What a process environment name may look like; anything else (`=`, spaces, NUL) never reaches a child process. */
export const ENV_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/;

/** The rule that reserves `key` (compared case-insensitively), or `undefined` when the key may be defined. */
export function reservedEnvKeyRule(key: string): ReservedEnvKeyRule | undefined {
  const name = key.toUpperCase();
  return RESERVED_ENV_KEYS.find((r) => (r.pattern.endsWith('*') ? name.startsWith(r.pattern.slice(0, -1)) : name === r.pattern));
}

export const isReservedEnvKey = (key: string): boolean => reservedEnvKeyRule(key) !== undefined;

/** The message of the API's `RESERVED_KEY` refusal and warning for `key`. */
export function reservedEnvKeyMessage(key: string, rule: ReservedEnvKeyRule): string {
  const why = rule.pattern.endsWith('*') ? `${rule.pattern}: ${rule.reason}` : rule.reason;
  return `${key} is a reserved name (${why}); Hangar never passes it to a build. Rename it if the app reads it, or set it on the build machine (CI variables, eas.json) if a tool needs it`;
}

/**
 * Splits a snapshot's variables (`clientExecution.processEnv`) into the ones that may reach the `eas` process and the
 * ones that may not: reserved names (except Hangar's own `HANGAR_SYSTEM_ENV_KEYS`), listed in `reserved` (names only,
 * sorted, never values), and keys that are not plain variable names, only counted in `invalid` (a key holding `=`
 * would set another variable; printing it could print a value).
 */
export function splitSnapshotEnv(values: Readonly<Record<string, string>>): { passed: Record<string, string>; reserved: string[]; invalid: number } {
  const passed: Record<string, string> = {};
  const reserved: string[] = [];
  let invalid = 0;
  for (const [key, value] of Object.entries(values)) {
    if (!ENV_NAME_PATTERN.test(key)) invalid += 1;
    else if (isReservedEnvKey(key) && !HANGAR_SYSTEM_ENV_KEYS.includes(key)) reserved.push(key);
    else passed[key] = value;
  }
  return { passed, reserved: reserved.sort(), invalid };
}
